import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';

const ORDER_STATUSES = [
  { value: 'pending', label: 'در انتظار پرداخت' },
  { value: 'pending_zarinpal', label: 'در انتظار تایید زرین‌پال' },
  { value: 'paid', label: 'پرداخت شده' },
  { value: 'completed', label: 'تکمیل شده' },
  { value: 'cancelled', label: 'لغو شده' },
  { value: 'refunded', label: 'بازگشت وجه' }
];

const CONSULTATION_STATUSES = [
  { value: 'pending', label: 'در انتظار' },
  { value: 'paid', label: 'پرداخت شده' },
  { value: 'scheduled', label: 'زمان‌بندی شده' },
  { value: 'completed', label: 'تکمیل شده' },
  { value: 'cancelled', label: 'لغو شده' }
];

const EditConsultation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    order_status: 'pending',
    consultation_status: 'pending',
    scheduled_at: '',
    admin_notes: ''
  });

  const [orderDetails, setOrderDetails] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${BASE_URL}/csrf-token`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setCsrfToken(d.csrfToken))
      .catch(() => setMessage('❌ خطا در دریافت توکن امنیتی'));
  }, []);

  useEffect(() => {
    if (!csrfToken) return;
    setFetchLoading(true);

    fetch(`${BASE_URL}/admin/consultation/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'CSRF-Token': csrfToken,
      },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(res => {
        if (!res.success) throw new Error(res.message);

        const data = res.data;
        setOrderDetails(data);

        setFormData({
          order_status: data.order_status || 'pending',
          consultation_status: data.consultation_status || 'pending',
          scheduled_at: data.scheduled_at ? data.scheduled_at.slice(0, 16) : '',
          admin_notes: data.admin_notes || ''
        });
      })
      .catch(err => setMessage('❌ ' + err.message))
      .finally(() => setFetchLoading(false));
  }, [csrfToken, id]);

  /* پارس امن JSON پرداخت */
  let paymentDetails = null;
  try {
    paymentDetails = orderDetails?.description
      ? JSON.parse(orderDetails.description)
      : null;
  } catch (e) {
    paymentDetails = null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsLoading(true);

    try {
      const res = await fetch(`${BASE_URL}/admin/consultation/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          order_id: orderDetails.order_id
        })
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'خطا در بروزرسانی');

      setMessage('✅ درخواست با موفقیت بروزرسانی شد');
      setTimeout(() => navigate('/admin/consultations'), 1500);

    } catch (err) {
      setMessage('❌ ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (fetchLoading) return <div className="loading">در حال بارگذاری...</div>;
  if (!orderDetails) return <div className="error">سفارش یافت نشد</div>;

  return (
    <div className="edit-consultation-container">
      <h2>ویرایش درخواست مشاوره #{id}</h2>

      {message && (
        <div className={message.includes("✅") ? "success-message" : "error-message"}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* اطلاعات مشتری */}
        <fieldset disabled style={{ background: '#f9f9f9', padding: 20, borderRadius: 8, marginBottom: 20 }}>
          <legend>📋 اطلاعات مشتری</legend>
          
          <div className="form-row">
            <div className="form-group">
              <label>نام و نام خانوادگی</label>
              <input type="text" value={orderDetails.full_name} readOnly />
            </div>

            <div className="form-group">
              <label>شماره تلفن</label>
              <input type="text" value={orderDetails.phone} readOnly />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ایمیل</label>
              <input type="email" value={orderDetails.email || '—'} readOnly />
            </div>

            <div className="form-group">
              <label>وبسایت</label>
              <input type="text" value={orderDetails.website_url || '—'} readOnly />
            </div>
          </div>

          <div className="form-group">
            <label>مشکل اصلی</label>
            <textarea value={orderDetails.main_problem || '—'} rows={3} readOnly />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>منبع ثبت</label>
              <input type="text" value={orderDetails.source || '—'} readOnly />
            </div>

            <div className="form-group">
              <label>تاریخ ثبت</label>
              <input 
                type="text" 
                value={new Date(orderDetails.consultation_created_at).toLocaleDateString('fa-IR')} 
                readOnly 
              />
            </div>
          </div>
        </fieldset>

        {/* اطلاعات مالی و پرداخت */}
        <fieldset disabled style={{ background: '#e3f2fd', padding: 20, borderRadius: 8, marginBottom: 20 }}>
          <legend>💰 اطلاعات مالی و پرداخت</legend>

          <div className="form-row">
            <div className="form-group">
              <label>مبلغ سفارش</label>
              <input 
                type="text" 
                value={Number(orderDetails.amount).toLocaleString() + ' تومان'} 
                readOnly 
              />
            </div>

            <div className="form-group">
              <label>روش پرداخت</label>
              <input 
                type="text" 
                value={orderDetails.payment_method === 'online' ? '💳 پرداخت آنلاین' : orderDetails.payment_method === 'card_to_card' ? '🏦 کارت به کارت' : '—'} 
                readOnly 
              />
            </div>
          </div>

          {orderDetails.zarinpal_ref_id && (
            <div className="form-row">
              <div className="form-group">
                <label>کد پیگیری زرین‌پال</label>
                <input type="text" value={orderDetails.zarinpal_ref_id} readOnly />
              </div>

              <div className="form-group">
                <label>Authority</label>
                <input 
                  type="text" 
                  value={orderDetails.zarinpal_authority} 
                  readOnly 
                  style={{ fontSize: '0.85em' }}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>تاریخ ثبت سفارش</label>
              <input 
                type="text" 
                value={new Date(orderDetails.order_created_at).toLocaleString('fa-IR')} 
                readOnly 
              />
            </div>

            <div className="form-group">
              <label>آخرین به‌روزرسانی</label>
              <input 
                type="text" 
                value={new Date(orderDetails.order_updated_at).toLocaleString('fa-IR')} 
                readOnly 
              />
            </div>
          </div>
        </fieldset>

        {/* ⭐ جزئیات پرداخت از JSON زرین‌پال */}
        {paymentDetails?.data && (
          <fieldset style={{ background: '#e8f5e9', padding: 20, borderRadius: 8, marginBottom: 20 }}>
            <legend>🧾 جزئیات پرداخت (زرین‌پال)</legend>

            <div className="form-row">
              <div className="form-group">
                <label>وضعیت پرداخت</label>
                <input type="text" value={paymentDetails.data.message} readOnly />
              </div>

              <div className="form-group">
                <label>کد وضعیت</label>
                <input type="text" value={paymentDetails.data.code} readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ref ID</label>
                <input type="text" value={paymentDetails.data.ref_id} readOnly />
              </div>

              <div className="form-group">
                <label>شماره کارت</label>
                <input type="text" value={paymentDetails.data.card_pan} readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>کارمزد زرین‌پال</label>
                <input
                  type="text"
                  value={Number(paymentDetails.data.fee).toLocaleString() + ' تومان'}
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>کارمزد شاپرک</label>
                <input
                  type="text"
                  value={Number(paymentDetails.data.shaparak_fee).toLocaleString() + ' تومان'}
                  readOnly
                />
              </div>
            </div>
          </fieldset>
        )}

        {/* وضعیت‌ها (قابل ویرایش) */}
        <fieldset style={{ background: '#fff3e0', padding: 20, borderRadius: 8, marginBottom: 20 }}>
          <legend>⚙️ مدیریت وضعیت</legend>

          <div className="form-row">
            <div className="form-group">
              <label>وضعیت سفارش *</label>
              <select 
                name="order_status" 
                value={formData.order_status} 
                onChange={handleChange}
              >
                {ORDER_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>وضعیت مشاوره *</label>
              <select 
                name="consultation_status" 
                value={formData.consultation_status} 
                onChange={handleChange}
              >
                {CONSULTATION_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>زمان جلسه</label>
            <input
              type="datetime-local"
              name="scheduled_at"
              value={formData.scheduled_at}
              onChange={handleChange}
            />
            <small style={{ color: '#666', display: 'block', marginTop: 5 }}>
              تاریخ و ساعت جلسه مشاوره را تعیین کنید
            </small>
          </div>

          <div className="form-group">
            <label>یادداشت ادمین</label>
            <textarea
              name="admin_notes"
              value={formData.admin_notes}
              onChange={handleChange}
              rows={4}
              placeholder="یادداشت‌های داخلی برای پیگیری..."
            />
            <small style={{ color: '#666', display: 'block', marginTop: 5 }}>
              💾 این یادداشت در ستون <code>admin_notes</code> جدول <code>consultation_requests</code> ذخیره می‌شود
            </small>
          </div>
        </fieldset>

        <div className="form-actions">
          <button type="submit" disabled={isLoading}>
            {isLoading ? '⏳ در حال ذخیره...' : '✅ ذخیره تغییرات'}
          </button>
          <button 
            type="button" 
            onClick={() => navigate("/admin/consultations")}
            style={{ background: '#999' }}
          >
            ↩ بازگشت
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditConsultation;
